import {inject, Injectable} from "@angular/core"
import {extractErrorInfo} from "@app/common/helpers/api/errors"
import {mutateThrowingErrors} from "@app/common/helpers/api/mutate"
import {MaterialAssetsRenderingService} from "@app/common/services/material-assets-rendering/material-assets-rendering.service"
import {NotificationsService} from "@app/common/services/notifications/notifications.service"
import {PermissionsService} from "@app/common/services/permissions/permissions.service"
import {MaterialOutputFragment} from "@app/platform/components/materials/material-outputs/material-outputs.generated"
import {FlatThumbnailOptionLabels} from "@app/platform/models/state-labels"
import {SceneManagerService} from "@app/template-editor/services/scene-manager.service"
import type {PbrExport} from "@cm/material-maps-exporter"
import {toPbrExportSettingsInput, toPbrExportType} from "@common/helpers/material-maps-exporter"
import {JobState, MaterialExportOutcome, MaterialOutputType, PbrExportType} from "@generated"
import {
    MaterialOutputsCancelJobGQL,
    MaterialOutputsCreateMaterialOutputGQL,
    MaterialOutputsDeleteDataObjectGQL,
    MaterialOutputsDeleteJsonFileGQL,
    MaterialOutputsDeleteOutputGQL,
    MaterialOutputsEnsurePconExportBatchGQL,
    MaterialOutputsEnsurePconExportGQL,
    MaterialOutputsEnsurePbrExportBatchGQL,
    MaterialOutputsEnsurePbrExportGQL,
    MaterialOutputsRestartJobGQL,
} from "@platform/services/material/material-output.generated"
import {environment} from "@environment"

type MaterialOutputPartial = Pick<MaterialOutputFragment, "id" | "job" | "result" | "config">

/**
 * What an export ensure came to, for callers that have to tell "a job is on its way" from "nothing
 * was created". A refusal is no longer thrown, so a caller holding a spinner cannot learn it from a
 * `catch` any more.
 */
export type MaterialExportOutcomeResult = {outcome: MaterialExportOutcome; jobId?: string}

const MATERIAL_EXPORT_GENERATING: MaterialExportOutcome[] = [MaterialExportOutcome.Generated, MaterialExportOutcome.Regenerated]

export const materialExportStartedGeneration = (result: MaterialExportOutcomeResult | undefined) =>
    result !== undefined && MATERIAL_EXPORT_GENERATING.includes(result.outcome)

@Injectable()
export class MaterialOutputService {
    readonly materialAssetsRenderingService = inject(MaterialAssetsRenderingService)
    readonly notifications = inject(NotificationsService)
    readonly permission = inject(PermissionsService)
    readonly sceneManagerService = inject(SceneManagerService)

    readonly cancelMaterialOutputJobGql = inject(MaterialOutputsCancelJobGQL)
    readonly createMaterialOutputGql = inject(MaterialOutputsCreateMaterialOutputGQL)
    readonly deleteDataObjectGql = inject(MaterialOutputsDeleteDataObjectGQL)
    readonly deleteJsonFileGql = inject(MaterialOutputsDeleteJsonFileGQL)
    readonly deleteMaterialOutputGql = inject(MaterialOutputsDeleteOutputGQL)
    readonly ensurePconExportGql = inject(MaterialOutputsEnsurePconExportGQL)
    readonly ensurePconExportBatchGql = inject(MaterialOutputsEnsurePconExportBatchGQL)
    readonly ensurePbrExportGql = inject(MaterialOutputsEnsurePbrExportGQL)
    readonly ensurePbrExportBatchGql = inject(MaterialOutputsEnsurePbrExportBatchGQL)
    readonly restartMaterialOutputJobGql = inject(MaterialOutputsRestartJobGQL)

    async createOutput(settings: {
        pbrExportSettings?: PbrExport.Types.Settings
        materialId: string
        materialRevisionId: string
        showNotification: boolean
        type: MaterialOutputType
        rethrowCaughtError?: boolean
    }): Promise<MaterialExportOutcomeResult | undefined> {
        const {pbrExportSettings, materialId, materialRevisionId, showNotification, type, rethrowCaughtError} = settings
        let jobId: string | undefined
        switch (type) {
            case MaterialOutputType.TileableImage: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateTile(
                        materialId,
                        environment.rendering.materialAssets.samples,
                        this.sceneManagerService,
                    )
                    if (showNotification) {
                        this.notifications.showInfo("Tileable image generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Tileable image generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.ShaderBall: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateShaderBallThumbnail(materialId, this.sceneManagerService)
                    if (showNotification) {
                        this.notifications.showInfo("Shader ball generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Shader ball generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.Thumbnail10x10cm:
            case MaterialOutputType.Thumbnail15x15cm:
            case MaterialOutputType.Thumbnail20x20cm:
            case MaterialOutputType.Thumbnail30x30cm:
            case MaterialOutputType.Thumbnail7x7inches:
            case MaterialOutputType.Thumbnail9x9inches:
            case MaterialOutputType.Thumbnail16x16inches:
            case MaterialOutputType.Thumbnail8x8inches:
            case MaterialOutputType.Thumbnail13_5x13_5inches:
            case MaterialOutputType.Thumbnail32x32inches:
            case MaterialOutputType.Thumbnail27x27inches: {
                try {
                    const flatOption = FlatThumbnailOptionLabels.get(type)?.state
                    if (flatOption && this.materialAssetsRenderingService.checkThumbnailSizeValid(flatOption.resolution, flatOption.size)) {
                        jobId = await this.materialAssetsRenderingService.generateThumbnail(
                            materialId,
                            environment.rendering.materialAssets.samples,
                            flatOption,
                            this.sceneManagerService,
                        )
                        if (showNotification) {
                            this.notifications.showInfo("Flat thumbnail generation: Job submitted", undefined, "Dismiss")
                        }
                    } else {
                        throw new Error("Requested resolution too high")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Flat thumbnail generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            case MaterialOutputType.PbrExportDefaultJpegLow:
            case MaterialOutputType.PbrExportDefaultJpegOriginal:
            case MaterialOutputType.PbrExportDefaultTiffLow:
            case MaterialOutputType.PbrExportDefaultTiffOriginal:
            case MaterialOutputType.PbrExportDefaultExrOriginal:
            case MaterialOutputType.PbrExportCustom: {
                // Return, not break: the mutation already created the output row alongside the job,
                // so falling through to createMaterialOutput below would create a duplicate row.
                try {
                    const result = await mutateThrowingErrors(this.ensurePbrExportGql)({
                        materialId,
                        type: toPbrExportType(type),
                        settings: pbrExportSettings ? toPbrExportSettingsInput(pbrExportSettings) : null,
                    })
                    return this.reportExportOutcome("PBR export", result.ensurePbrExport, showNotification)
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showErrorString(`PBR export generation: Failed (${extractErrorInfo(error).message})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                    return undefined
                }
            }
            case MaterialOutputType.PconExport: {
                // Return, not break: the mutation already created the output row alongside the job,
                // so falling through to createMaterialOutput below would create a duplicate row.
                try {
                    const result = await mutateThrowingErrors(this.ensurePconExportGql)({materialId})
                    return this.reportExportOutcome("pCon export", result.ensurePconExport, showNotification)
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showErrorString(`pCon export generation: Failed (${extractErrorInfo(error).message})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                    return undefined
                }
            }
            case MaterialOutputType.FabricOverview: {
                try {
                    jobId = await this.materialAssetsRenderingService.generateFabricOverview(materialId)
                    if (showNotification) {
                        this.notifications.showInfo("Fabric overview generation: Job submitted", undefined, "Dismiss")
                    }
                } catch (error) {
                    if (showNotification) {
                        this.notifications.showInfo(`Fabric overview generation: Failed (${error})`)
                    }
                    if (rethrowCaughtError) {
                        throw error
                    }
                }
                break
            }
            default:
                break
        }

        if (jobId) {
            await mutateThrowingErrors(this.createMaterialOutputGql)({
                input: {
                    jobId,
                    materialId,
                    materialRevisionId,
                    type,
                },
            })
        } else {
            if (showNotification) {
                this.notifications.showInfo("Error creating job", 3000)
            }
        }
        return undefined
    }

    /**
     * One snackbar per outcome. A refusal carries the sentence the server wrote for whoever clicked —
     * which is the point of returning it as data: a thrown refusal is a 409, and the web app's error
     * mapping replaces every 409's message with generic text.
     */
    private reportExportOutcome(
        label: string,
        result: {outcome: MaterialExportOutcome; job?: {id: string} | null; refusal?: {message: string} | null},
        showNotification: boolean,
    ): MaterialExportOutcomeResult {
        if (showNotification) {
            switch (result.outcome) {
                case MaterialExportOutcome.Generated:
                case MaterialExportOutcome.Regenerated:
                    this.notifications.showInfo(`${label} generation: Job submitted (${result.job?.id})`)
                    break
                case MaterialExportOutcome.AlreadyRunning:
                    this.notifications.showInfo(`${label} is already being generated`)
                    break
                case MaterialExportOutcome.UpToDate:
                    this.notifications.showInfo(`${label} is already up to date`)
                    break
                case MaterialExportOutcome.Refused:
                    this.notifications.showErrorString(`${label} generation: ${result.refusal?.message ?? "Refused"}`)
                    break
            }
        }
        return {outcome: result.outcome, jobId: result.job?.id ?? undefined}
    }

    async ensurePbrExportBatch(materialIds: string[], type: PbrExportType) {
        return (await mutateThrowingErrors(this.ensurePbrExportBatchGql)({materialIds, type})).ensurePbrExportBatch
    }

    async ensurePconExportBatch(materialIds: string[]) {
        return (await mutateThrowingErrors(this.ensurePconExportBatchGql)({materialIds})).ensurePconExportBatch
    }

    async cancelAndRemoveOutput({materialOutput, showNotification}: {materialOutput: MaterialOutputPartial; showNotification: boolean}) {
        if (materialOutput.job?.state === JobState.Init || materialOutput.job?.state === JobState.Runnable || materialOutput.job?.state === JobState.Running) {
            await mutateThrowingErrors(this.cancelMaterialOutputJobGql)({id: materialOutput.job.id})
        }

        if (materialOutput.config) {
            await mutateThrowingErrors(this.deleteJsonFileGql)({id: materialOutput.config.id}).catch(() => {
                if (showNotification) {
                    this.notifications.showInfo("Cannot delete config.", 3000)
                }
            })
        }

        if (materialOutput.result) {
            await mutateThrowingErrors(this.deleteDataObjectGql)({id: materialOutput.result.id}).catch(() => {
                if (showNotification) {
                    this.notifications.showInfo("Cannot delete file.", 3000)
                }
            })
        } else {
            // referential integrity should take care of this at the DB level when the result is deleted
            // so we only need to delete explicitly when there is no result
            await mutateThrowingErrors(this.deleteMaterialOutputGql)({id: materialOutput.id})
        }
    }

    async restartOutput({materialOutput, showNotification}: {materialOutput: MaterialOutputPartial; showNotification: boolean}) {
        if (materialOutput.job?.id) {
            await mutateThrowingErrors(this.restartMaterialOutputJobGql)({id: materialOutput.job.id})
        } else {
            if (showNotification) {
                this.notifications.showInfo("Cannot restart job without job id", 3000)
            }
        }
    }
}
